import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Copy,
  Eye,
  Grid2X2,
  ImageIcon,
  List,
  MoreHorizontal,
  Pencil,
  Play,
  Plus,
  Save,
  Search,
  Send,
  SlidersHorizontal,
  Trash2,
  X,
} from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { Button, EmptyState, ErrorState, LoadingState, StatusBadge } from "../components/UI";
import { api } from "../lib/api";
import { Link } from "../lib/router";
import type { Question, QuestionDetail, QuestionUpdateInput } from "../lib/types";

type EditableStatus = QuestionUpdateInput["status"];

export function QuestionBank() {
  const client = useQueryClient();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [difficulty, setDifficulty] = useState("all");
  const [view, setView] = useState<"table" | "grid">("table");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [preview, setPreview] = useState<Question | null>(null);
  const [edit, setEdit] = useState<Question | null>(null);
  const [manage, setManage] = useState<Question | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Question | null>(null);
  const [play, setPlay] = useState<Question | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const pageSize = 100;

  useEffect(() => { setPage(1); }, [search, status, difficulty]);
  const query = useQuery({ queryKey: ["questions", search, status, difficulty, page], queryFn: () => api.questions(search, status, difficulty, page, pageSize) });
  const groups = useQuery({ queryKey: ["groups"], queryFn: api.groups });
  const detail = useQuery({
    queryKey: ["question", edit?.id],
    queryFn: () => api.question(edit!.id),
    enabled: Boolean(edit),
  });
  const pageData = query.data ?? { items: [], total: 0, page, pageSize, totalPages: 1 };
  const questions = pageData.items;

  const duplicate = useMutation({
    mutationFn: api.duplicateQuestion,
    onSuccess: async () => {
      setNotice("Salinan draft berhasil dibuat.");
      await client.invalidateQueries({ queryKey: ["questions"] });
    },
  });
  const statusMutation = useMutation({
    mutationFn: ({ id, status: next }: { id: string; status: EditableStatus }) => api.updateQuestionStatus(id, next),
    onSuccess: async () => {
      setManage(null);
      setNotice("Status pertanyaan berhasil diperbarui.");
      await client.invalidateQueries({ queryKey: ["questions"] });
    },
  });
  const update = useMutation({
    mutationFn: ({ id, input }: { id: string; input: QuestionUpdateInput }) => api.updateQuestion(id, input),
    onSuccess: async () => {
      setEdit(null);
      setNotice("Perubahan soal berhasil disimpan.");
      await client.invalidateQueries({ queryKey: ["questions"] });
    },
  });
  const remove = useMutation({
    mutationFn: api.deleteQuestion,
    onSuccess: async () => {
      setDeleteTarget(null);
      setNotice("Soal berhasil dihapus dari Bank Soal.");
      await client.invalidateQueries({ queryKey: ["questions"] });
    },
  });
  const start = useMutation({
    mutationFn: ({ chatId, questionId }: { chatId: string; questionId: string }) => api.startGame(chatId, questionId),
    onSuccess: () => {
      setPlay(null);
      setNotice("Ronde berhasil dikirim ke grup Telegram.");
    },
  });

  const reset = () => {
    setSearch("");
    setStatus("all");
    setDifficulty("all");
  };

  return (
    <div className="question-bank-page">
      <div className="dashboard-intro bank-intro">
        <p>Kelola pertanyaan, varian clue, dan status publikasi secara terpusat.</p>
        <Link to="/questions/new"><Button icon={Plus}>Tambah soal</Button></Link>
      </div>

      {notice && (
        <div className="connect-banner compact-banner">
          <span><CheckIcon /></span>
          <div><strong>{notice}</strong><p>Aksi telah diproses oleh server.</p></div>
          <button onClick={() => setNotice(null)}><X size={15} /></button>
        </div>
      )}

      <div className="page-toolbar">
        <label className="search-field">
          <Search size={15} />
          <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Cari jawaban atau kategori..." />
        </label>
        <select className="filter-select" value={status} onChange={(event) => setStatus(event.target.value)}>
          <option value="all">Semua status</option>
          <option value="published">Published</option>
          <option value="draft">Draft</option>
          <option value="disabled">Disabled</option>
          <option value="archived">Archived</option>
        </select>
        <select className="filter-select" value={difficulty} onChange={(event) => setDifficulty(event.target.value)}>
          <option value="all">Semua tingkat</option>
          <option value="easy">Easy</option>
          <option value="normal">Normal</option>
          <option value="hard">Hard</option>
        </select>
        <button className="button secondary" onClick={() => setFiltersOpen(!filtersOpen)}>
          <SlidersHorizontal size={15} />{filtersOpen ? "Tutup filter" : "Filter"}
        </button>
        <span className="toolbar-separator" />
        <div className="view-toggle">
          <button aria-label="Tampilan tabel" className={view === "table" ? "active" : ""} onClick={() => setView("table")}><List size={15} /></button>
          <button aria-label="Tampilan grid" className={view === "grid" ? "active" : ""} onClick={() => setView("grid")}><Grid2X2 size={14} /></button>
        </div>
      </div>

      {filtersOpen && <div className="filter-summary"><span>{query.data?.total || 0} pertanyaan cocok</span><button onClick={reset}>Reset filter</button></div>}

      {query.isLoading ? (
        <div className="table-card"><LoadingState /></div>
      ) : query.isError ? (
        <div className="table-card"><ErrorState message={query.error.message} retry={() => void query.refetch()} /></div>
      ) : !questions.length ? (
        <div className="table-card"><EmptyState title="Bank soal masih kosong" description="Unggah satu foto, tentukan area clue, lalu publikasikan soal pertama Anda." action={<Link to="/questions/new"><Button icon={Plus}>Buat soal pertama</Button></Link>} /></div>
      ) : view === "grid" ? (
        <><div className="question-grid">
          {questions.map((question) => (
            <article className="question-grid-card" key={question.id}>
              <div className="grid-card-media">
                {question.thumbnailUrl ? <img src={question.thumbnailUrl} alt="" /> : <ImageIcon size={26} />}
                <StatusBadge status={question.status} />
              </div>
              <div className="grid-card-body">
                <span className="question-meta">{question.category || "Tanpa kategori"} · {question.difficulty}</span>
                <h3>{question.displayAnswer}</h3>
                <p>{question.variants} varian · {question.basePoints} poin</p>
                <div>
                  <button onClick={() => setPreview(question)}><Eye size={15} /> Preview</button>
                  <button title="Edit soal" onClick={() => setEdit(question)}><Pencil size={15} /></button>
                  <button title="Kelola status" onClick={() => setManage(question)}><MoreHorizontal size={15} /></button>
                  <button className="danger-action" title="Hapus soal" onClick={() => setDeleteTarget(question)}><Trash2 size={15} /></button>
                </div>
              </div>
            </article>
          ))}
        </div><QuestionPagination page={pageData.page} totalPages={pageData.totalPages} total={pageData.total} pageSize={pageData.pageSize} onChange={setPage} /></>
      ) : (
        <div className="table-card">
          <table className="data-table question-table">
            <thead><tr><th style={{ width: 42 }}><input className="checkbox" type="checkbox" disabled /></th><th style={{ width: "28%" }}>Pertanyaan</th><th>Status</th><th>Difficulty</th><th>Varian</th><th>Poin</th><th>Diperbarui</th><th style={{ width: 210, textAlign: "right" }}>Aksi</th></tr></thead>
            <tbody>
              {questions.map((question) => (
                <tr key={question.id}>
                  <td><input className="checkbox" type="checkbox" aria-label={`Pilih ${question.displayAnswer}`} /></td>
                  <td><div className="question-cell"><span className="question-thumb">{question.thumbnailUrl ? <img src={question.thumbnailUrl} alt="" /> : <ImageIcon size={16} />}</span><span><strong>{question.displayAnswer}</strong><small>{question.category || "Tanpa kategori"}</small></span></div></td>
                  <td><StatusBadge status={question.status} /></td>
                  <td><span className={`difficulty ${question.difficulty}`}><i />{question.difficulty}</span></td>
                  <td>{question.variants} clue</td>
                  <td>{question.basePoints}</td>
                  <td>{new Intl.DateTimeFormat("id-ID", { day: "2-digit", month: "short", year: "numeric" }).format(new Date(question.updatedAt))}</td>
                  <td><div className="row-actions">
                    <button title="Preview" onClick={() => setPreview(question)}><Eye size={15} /></button>
                    <button title="Edit soal" onClick={() => setEdit(question)}><Pencil size={15} /></button>
                    <button title="Kelola status" onClick={() => setManage(question)}><MoreHorizontal size={15} /></button>
                    <button title="Preview Telegram" onClick={() => setPreview(question)}><Send size={15} /></button>
                    <button title="Mulai game" onClick={() => setPlay(question)} disabled={question.status !== "published"}><Play size={15} /></button>
                    <button title="Duplikat" onClick={() => duplicate.mutate(question.id)}><Copy size={15} /></button>
                    <button className="danger-action" title="Hapus soal" onClick={() => setDeleteTarget(question)}><Trash2 size={15} /></button>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
          <QuestionPagination page={pageData.page} totalPages={pageData.totalPages} total={pageData.total} pageSize={pageData.pageSize} onChange={setPage} />
        </div>
      )}

      {preview && (
        <div className="modal-layer" onMouseDown={() => setPreview(null)}>
          <div className="question-preview-modal" onMouseDown={(event) => event.stopPropagation()}>
            <header><div><p className="eyebrow">Telegram preview</p><h3>{preview.displayAnswer}</h3></div><button className="icon-button ghost" onClick={() => setPreview(null)}><X size={17} /></button></header>
            <div className="telegram-preview-stage">{preview.thumbnailUrl ? <img src={preview.thumbnailUrl} alt={`Clue ${preview.displayAnswer}`} /> : <ImageIcon size={38} />}<div><strong>SIAPA DIA?</strong><p>Tebak siapa orang pada potongan foto ini.</p><small>{preview.category || "Umum"} · {preview.difficulty} · {preview.basePoints} poin</small></div></div>
            <footer><Button variant="secondary" onClick={() => setPreview(null)}>Tutup</Button>{preview.status === "published" && <Button icon={Play} onClick={() => { setPreview(null); setPlay(preview); }}>Mulai game</Button>}</footer>
          </div>
        </div>
      )}

      {edit && (
        <div className="modal-layer edit-modal-layer" onMouseDown={() => setEdit(null)}>
          {detail.isLoading ? (
            <div className="question-preview-modal"><LoadingState /></div>
          ) : detail.isError ? (
            <div className="question-preview-modal"><ErrorState message={detail.error.message} retry={() => void detail.refetch()} /><footer><Button variant="secondary" onClick={() => setEdit(null)}>Tutup</Button></footer></div>
          ) : detail.data ? (
            <QuestionEditModal
              key={detail.data.updatedAt}
              detail={detail.data}
              pending={update.isPending}
              error={update.isError ? update.error.message : null}
              onClose={() => setEdit(null)}
              onSubmit={(input) => update.mutate({ id: edit.id, input })}
            />
          ) : null}
        </div>
      )}

      {manage && (
        <div className="modal-layer" onMouseDown={() => setManage(null)}>
          <div className="question-preview-modal" onMouseDown={(event) => event.stopPropagation()}>
            <header><div><p className="eyebrow">Question lifecycle</p><h3>{manage.displayAnswer}</h3></div><button className="icon-button ghost" onClick={() => setManage(null)}><X size={17} /></button></header>
            <div className="modal-action-list">
              <button disabled={statusMutation.isPending} onClick={() => statusMutation.mutate({ id: manage.id, status: "published" })}>Publikasikan<span>Siap dipilih untuk game</span></button>
              <button disabled={statusMutation.isPending} onClick={() => statusMutation.mutate({ id: manage.id, status: "draft" })}>Jadikan draft<span>Sembunyikan dari pemilihan ronde</span></button>
              <button disabled={statusMutation.isPending} onClick={() => statusMutation.mutate({ id: manage.id, status: "disabled" })}>Nonaktifkan<span>Pertahankan data tanpa dimainkan</span></button>
              <button onClick={() => { duplicate.mutate(manage.id); setManage(null); }}>Duplikat<span>Buat salinan draft</span></button>
              {statusMutation.isError && <p className="inline-error">{statusMutation.error.message}</p>}
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="modal-layer" onMouseDown={() => setDeleteTarget(null)}>
          <div className="question-preview-modal delete-question-modal" onMouseDown={(event) => event.stopPropagation()} role="alertdialog" aria-modal="true" aria-labelledby="delete-question-title">
            <header><div><p className="eyebrow danger-eyebrow">Hapus pertanyaan</p><h3 id="delete-question-title">Konfirmasi penghapusan</h3></div><button className="icon-button ghost" onClick={() => setDeleteTarget(null)}><X size={17} /></button></header>
            <div className="delete-confirmation">
              <span className="delete-warning-icon"><AlertTriangle size={22} /></span>
              <div><strong>{deleteTarget.displayAnswer}</strong><p>Soal akan hilang dari Bank Soal dan tidak dapat dipilih untuk ronde berikutnya. Riwayat game yang sudah selesai tetap aman.</p></div>
            </div>
            {remove.isError && <p className="inline-error delete-error">{remove.error.message}</p>}
            <footer><Button variant="secondary" disabled={remove.isPending} onClick={() => setDeleteTarget(null)}>Batal</Button><Button variant="danger" icon={Trash2} disabled={remove.isPending} onClick={() => remove.mutate(deleteTarget.id)}>{remove.isPending ? "Menghapus..." : "Hapus soal"}</Button></footer>
          </div>
        </div>
      )}

      {play && (
        <div className="modal-layer" onMouseDown={() => setPlay(null)}>
          <div className="question-preview-modal" onMouseDown={(event) => event.stopPropagation()}>
            <header><div><p className="eyebrow">Pilih grup Telegram</p><h3>Kirim “{play.displayAnswer}”</h3></div><button className="icon-button ghost" onClick={() => setPlay(null)}><X size={17} /></button></header>
            <div className="modal-action-list">
              {groups.isLoading ? <LoadingState /> : !groups.data?.length ? <EmptyState title="Belum ada grup" description="Aktifkan bot dengan /start@siapadiabot di grup." /> : groups.data.filter((item) => item.status === "connected").map((group) => <button key={String(group.id)} disabled={start.isPending} onClick={() => start.mutate({ chatId: String(group.telegram_chat_id), questionId: play.id })}>{String(group.title)}<span>{String(group.telegram_chat_id)}</span></button>)}
              {start.isError && <p className="inline-error">{start.error.message}</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function QuestionPagination({ page, totalPages, total, pageSize, onChange }: { page: number; totalPages: number; total: number; pageSize: number; onChange: (page: number) => void }) {
  const start = total ? (page - 1) * pageSize + 1 : 0;
  const end = Math.min(total, page * pageSize);
  const first = Math.max(1, Math.min(page - 2, totalPages - 4));
  const pages = Array.from({ length: Math.min(5, totalPages) }, (_, index) => first + index).filter((value) => value <= totalPages);
  return <div className="table-footer"><span>Menampilkan {start}–{end} dari {total} pertanyaan</span><div className="pagination"><button disabled={page <= 1} onClick={() => onChange(page - 1)}>‹</button>{pages.map((value) => <button key={value} className={value === page ? "active" : ""} onClick={() => onChange(value)}>{value}</button>)}<button disabled={page >= totalPages} onClick={() => onChange(page + 1)}>›</button></div></div>;
}

function QuestionEditModal({ detail, pending, error, onClose, onSubmit }: { detail: QuestionDetail; pending: boolean; error: string | null; onClose: () => void; onSubmit: (input: QuestionUpdateInput) => void }) {
  const [answer, setAnswer] = useState(detail.answer);
  const [aliases, setAliases] = useState(detail.aliases.join("\n"));
  const [category, setCategory] = useState(detail.category);
  const [difficulty, setDifficulty] = useState(detail.difficulty);
  const [status, setStatus] = useState<QuestionUpdateInput["status"]>(detail.status);
  const [points, setPoints] = useState(detail.points);
  const [duration, setDuration] = useState(detail.duration);
  const [clueText, setClueText] = useState(detail.clueText);
  const [traitsText, setTraitsText] = useState(detail.traitsText);
  const [region, setRegion] = useState(detail.region);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const parsedAliases = aliases.split(/[\n,;]+/).map((item) => item.trim()).filter(Boolean);
    onSubmit({ answer: answer.trim(), aliases: parsedAliases, category: category.trim(), difficulty, status, points, duration, clueText: clueText.trim(), traitsText: traitsText.trim(), region });
  };

  return (
    <form className="question-preview-modal question-edit-modal" onMouseDown={(event) => event.stopPropagation()} onSubmit={submit}>
      <header><div><p className="eyebrow">Edit pertanyaan</p><h3>{detail.answer}</h3></div><button type="button" className="icon-button ghost" onClick={onClose}><X size={17} /></button></header>
      <div className="question-edit-body">
        <div className="question-edit-media">
          {detail.thumbnailUrl ? <img src={detail.thumbnailUrl} alt={`Clue ${detail.answer}`} /> : <ImageIcon size={28} />}
          <div><strong>Clue aktif</strong><span>Area: {regionLabel(region)}</span></div>
        </div>
        <div className="question-edit-grid">
          <label className="field wide"><span>Jawaban utama atau alternatif</span><input required maxLength={120} value={answer} onChange={(event) => setAnswer(event.target.value)} placeholder="Contoh: Rendi Widanov atau Rendi" /><small className="field-help">Gunakan kata “atau” untuk beberapa jawaban utama.</small></label>
          <label className="field wide"><span>Alias jawaban</span><textarea value={aliases} onChange={(event) => setAliases(event.target.value)} placeholder={"Satu alias per baris\nContoh: Rendi"} /><small className="field-help">Semua alias ikut dianggap benar dan tidak membedakan huruf besar atau kecil.</small></label>
          <label className="field wide"><span>Clue teks</span><textarea maxLength={180} value={clueText} onChange={(event) => setClueText(event.target.value)} placeholder="Contoh: Figur publik Indonesia yang dikenal lewat acara komedi." /><small className="field-help">Ditampilkan untuk kategori selain SSG agar pemain lebih mudah menebak.</small></label>
          <label className="field wide"><span>Ciri-ciri</span><textarea maxLength={180} value={traitsText} onChange={(event) => setTraitsText(event.target.value)} placeholder="Contoh: Nama terdiri dari dua kata dan berawalan A." /></label>
          <label className="field"><span>Kategori</span><input maxLength={80} value={category} onChange={(event) => setCategory(event.target.value)} placeholder="Figur publik" /></label>
          <label className="field"><span>Difficulty</span><select value={difficulty} onChange={(event) => setDifficulty(event.target.value as QuestionDetail["difficulty"])}><option value="easy">Easy</option><option value="normal">Normal</option><option value="hard">Hard</option></select></label>
          <label className="field"><span>Poin</span><input type="number" min={1} max={1000} value={points} onChange={(event) => setPoints(Number(event.target.value))} /></label>
          <label className="field"><span>Durasi (detik)</span><input type="number" min={10} max={600} value={duration} onChange={(event) => setDuration(Number(event.target.value))} /></label>
          <label className="field"><span>Status</span><select value={status} onChange={(event) => setStatus(event.target.value as QuestionUpdateInput["status"])}><option value="published">Published</option><option value="draft">Draft</option><option value="disabled">Disabled</option><option value="archived">Archived</option></select></label>
          <label className="field"><span>Tipe area clue</span><select value={region} onChange={(event) => setRegion(event.target.value as QuestionDetail["region"])}><option value="eyes">Mata</option><option value="nose">Hidung</option><option value="lips">Bibir</option><option value="chin">Dagu</option><option value="hair">Rambut</option><option value="left_ear">Telinga kiri</option><option value="right_ear">Telinga kanan</option><option value="custom">Custom</option></select></label>
        </div>
        {error && <p className="inline-error">{error}</p>}
      </div>
      <footer><Button type="button" variant="secondary" disabled={pending} onClick={onClose}>Batal</Button><Button type="submit" icon={Save} disabled={pending || !answer.trim()}>{pending ? "Menyimpan..." : "Simpan perubahan"}</Button></footer>
    </form>
  );
}

function regionLabel(region: QuestionDetail["region"]) {
  return ({ eyes: "Mata", nose: "Hidung", lips: "Bibir", chin: "Dagu", hair: "Rambut", left_ear: "Telinga kiri", right_ear: "Telinga kanan", custom: "Custom" } as const)[region];
}

function CheckIcon() {
  return <span style={{ fontWeight: 800 }}>✓</span>;
}
